import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Plus, Zap } from "lucide-react";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import MenuRowButton from "../ui/MenuRowButton";
import Tooltip from "../ui/Tooltip";
import { useBodies, useJoints } from "../../stores/sceneStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { addDefaultJoint } from "../../utils/bodyAddActions";
import { INSPECTOR_CHROME_SECTION_ICON_CLASS } from "../../utils/inspectorChromeButtonClasses";
import { getJointTypeMenuOptions } from "../../utils/jointTypeValidation";
import { stripNamespacePrefix as stripPrefix } from "../../utils/nameUtils";
import { BallJointTypeIcon, FreeJointTypeIcon, JointIcon, SlideJointTypeIcon } from "../ui/Icons";
import {
  applyJointLabelDragViaActuatorPositionControls,
  getJointPositionActuatorSliderState,
} from "../../physics/jointLabelActuatorDrag";

const EMPTY_LIST = Object.freeze([]);
const BODY_ADD_MENU_BUTTON_CLASS =
  "menu-item ring-0 focus-visible:ring-0 focus-visible:outline-none shrink-0 h-8 w-8 rounded-md border flex items-center justify-center panel-text-helper transition-colors hover:bg-gray-700/60 hover:text-gray-200";
const BODY_ADD_MENU_DISABLED_BUTTON_CLASS =
  "menu-item ring-0 focus-visible:ring-0 focus-visible:outline-none shrink-0 h-8 w-8 rounded-md border flex items-center justify-center panel-text-helper cursor-not-allowed opacity-35";
const BODY_ADD_MENU_BORDER_STYLE = { backgroundColor: "transparent", borderColor: "rgb(55 65 81)" };

const buildJointRowsSnapshot = (allJoints, jointUuids) => {
  if (!Array.isArray(jointUuids) || jointUuids.length === 0) return EMPTY_LIST;
  const rows = [];
  for (let index = 0; index < jointUuids.length; index += 1) {
    const jointUuid = jointUuids[index];
    const jointData = allJoints?.[jointUuid];
    if (jointData?.kind === "free") continue;
    if (!jointData || !jointData.name) continue;
    const [minVal, maxVal] = jointData.range ?? [-Math.PI, Math.PI];
    rows.push({
      jointUuid,
      jointName: stripPrefix(jointData.name),
      minVal,
      maxVal,
      currentValue: jointData.ref ?? 0,
    });
  }
  return rows;
};

const areJointRowsEqual = (prev, next) => {
  if (prev === next) return true;
  if (!prev || !next) return false;
  if (prev.length !== next.length) return false;
  for (let index = 0; index < prev.length; index += 1) {
    const prevRow = prev[index];
    const nextRow = next[index];
    if (
      prevRow.jointUuid !== nextRow.jointUuid ||
      prevRow.jointName !== nextRow.jointName ||
      prevRow.minVal !== nextRow.minVal ||
      prevRow.maxVal !== nextRow.maxVal ||
      prevRow.currentValue !== nextRow.currentValue
    ) {
      return false;
    }
  }
  return true;
};

const areActuatorSliderStatesEqual = (prev, next) => {
  if (prev === next) return true;
  if (!prev || !next) return false;
  return (
    prev.controlKey === next.controlKey &&
    prev.value === next.value &&
    prev.min === next.min &&
    prev.max === next.max &&
    prev.step === next.step &&
    prev.actuatorDisplayLabel === next.actuatorDisplayLabel
  );
};

const BodyPanelJointControlRow = React.memo(function BodyPanelJointControlRow({
  row,
  isSimRunning,
  robotActions,
  selectionActions,
}) {
  const actuatorSliderState = useStoreWithEqualityFn(
    usePhysicsStore,
    (s) =>
      isSimRunning
        ? getJointPositionActuatorSliderState(row.jointUuid, s.actuatorPositionTargets)
        : null,
    areActuatorSliderStatesEqual
  );
  const useActuatorSlider = isSimRunning && actuatorSliderState !== null;
  const min = useActuatorSlider ? actuatorSliderState.min : row.minVal;
  const max = useActuatorSlider ? actuatorSliderState.max : row.maxVal;
  const step = useActuatorSlider
    ? actuatorSliderState.step
    : (row.maxVal - row.minVal) / 100 || 0.01;
  const sliderValue = useActuatorSlider ? actuatorSliderState.value : row.currentValue;

  const handleSliderChange = useCallback(
    (event) => {
      const next = parseFloat(event.target.value);
      if (Number.isNaN(next)) return;
      if (isSimRunning && applyJointLabelDragViaActuatorPositionControls(row.jointUuid, next))
        return;
      robotActions.updateJointValue(row.jointUuid, next);
    },
    [isSimRunning, robotActions, row.jointUuid]
  );

  const sliderTooltip = useActuatorSlider
    ? "Adjust position actuator target while physics is running"
    : isSimRunning
      ? "Add a position actuator on this joint, or stop physics to edit the joint value"
      : null;

  const rightContent = useActuatorSlider ? (
    <span className="text-xs text-gray-400 select-none inline-flex items-center gap-1">
      <Zap className="w-3 h-3 shrink-0 text-amber-500" aria-hidden />
      Target: {sliderValue.toFixed(3)}
    </span>
  ) : (
    <span className="text-xs text-gray-400 select-none">
      Current: {row.currentValue.toFixed(3)}
    </span>
  );

  const rowTitle = useActuatorSlider ? actuatorSliderState.actuatorDisplayLabel : row.jointName;

  return (
    <div className="flex flex-col gap-1">
      <MenuRowButton
        className="mb-1"
        onClick={() => selectionActions.selectJoint(row.jointUuid)}
        rightContent={rightContent}
      >
        {rowTitle}
      </MenuRowButton>
      <Tooltip content={sliderTooltip} fullWidth>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={sliderValue}
          onChange={handleSliderChange}
          className={`w-full h-2 slider ${isSimRunning && !useActuatorSlider ? "cursor-not-allowed opacity-60" : ""}`}
          disabled={isSimRunning && !useActuatorSlider}
          style={useActuatorSlider ? { accentColor: "#f97316" } : undefined}
          aria-label={
            useActuatorSlider
              ? `Actuator target for ${actuatorSliderState.actuatorDisplayLabel}`
              : `Joint value for ${row.jointName}`
          }
        />
      </Tooltip>
    </div>
  );
});

const BodyJointsSection = React.memo(function BodyJointsSection({
  jointUuids,
  isSimRunning,
  robotActions,
  selectionActions,
}) {
  const jointRows = useJoints(
    (allJoints) => buildJointRowsSnapshot(allJoints, jointUuids),
    areJointRowsEqual
  );
  if (!jointRows.length) return null;

  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger quiet>Joints (upstream) ({jointRows.length})</CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2">
          {jointRows.map((row) => (
            <BodyPanelJointControlRow
              key={row.jointUuid}
              row={row}
              isSimRunning={isSimRunning}
              robotActions={robotActions}
              selectionActions={selectionActions}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

const ChildBodyJointsSection = React.memo(function ChildBodyJointsSection({
  childBodies,
  isSimRunning,
  physicsAuthoringLocked,
  robotActions,
  selectionActions,
}) {
  const [addJointRowOpen, setAddJointRowOpen] = useState(false);
  const bodies = useBodies();
  const joints = useJoints();
  const groups = useJoints(
    (allJoints) =>
      (childBodies || []).map((childBody) => ({
        childUuid: childBody.uuid,
        childName: stripPrefix(childBody.name),
        rows: buildJointRowsSnapshot(allJoints, childBody.joints || EMPTY_LIST),
      })),
    (prev, next) => {
      if (prev === next) return true;
      if (!prev || !next) return false;
      if (prev.length !== next.length) return false;
      for (let index = 0; index < prev.length; index += 1) {
        const prevGroup = prev[index];
        const nextGroup = next[index];
        if (
          prevGroup.childUuid !== nextGroup.childUuid ||
          prevGroup.childName !== nextGroup.childName ||
          !areJointRowsEqual(prevGroup.rows, nextGroup.rows)
        ) {
          return false;
        }
      }
      return true;
    }
  );
  const childJointCount = groups.reduce((sum, group) => sum + group.rows.length, 0);

  const addJointTargetBodyUuid = groups[0]?.childUuid || null;
  const canAddJoint = !physicsAuthoringLocked && !!addJointTargetBodyUuid;
  const addJointOptions = useMemo(
    () =>
      getJointTypeMenuOptions({
        bodies,
        joints,
        bodyUuid: addJointTargetBodyUuid,
        includeFree: false,
      }),
    [addJointTargetBodyUuid, bodies, joints]
  );

  useEffect(() => {
    setAddJointRowOpen(false);
  }, [addJointTargetBodyUuid]);

  const handleAddJointByKind = (kind) => {
    if (!canAddJoint) return;
    const created = addDefaultJoint(robotActions, addJointTargetBodyUuid, kind);
    if (!created) return;
    setAddJointRowOpen(false);
  };

  if (!groups.length) return null;

  return (
    <Collapsible defaultOpen>
      <CollapsibleTrigger
        quiet
        aria-label="Toggle Child Joints"
        actions={
          <Tooltip
            content={
              physicsAuthoringLocked
                ? "Stop physics to add a joint"
                : addJointRowOpen
                  ? "Hide joint type menu"
                  : "Add child joint"
            }
          >
            <button
              type="button"
              disabled={!canAddJoint}
              aria-label="Add child joint"
              aria-pressed={addJointRowOpen}
              onClick={() => {
                if (!canAddJoint) return;
                setAddJointRowOpen((open) => !open);
              }}
              className={INSPECTOR_CHROME_SECTION_ICON_CLASS}
              style={{
                opacity: !canAddJoint ? 0.35 : addJointRowOpen ? 1 : 0.55,
              }}
            >
              <Plus size={16} aria-hidden="true" />
            </button>
          </Tooltip>
        }
      >
        <span className="min-w-0 truncate text-left">Joints (children) ({childJointCount})</span>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2">
          {addJointRowOpen ? (
            <BodyJointTypeInlineMenu
              disabled={!canAddJoint}
              options={addJointOptions}
              onAddJointKind={handleAddJointByKind}
            />
          ) : null}
          {groups.map((group) => (
            <div key={`${group.childUuid}-joints`} className="mb-2">
              {group.rows.map((row) => (
                <BodyPanelJointControlRow
                  key={`${group.childUuid}-${row.jointUuid}`}
                  row={row}
                  isSimRunning={isSimRunning}
                  robotActions={robotActions}
                  selectionActions={selectionActions}
                />
              ))}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
});

const BodyJointTypeInlineMenu = React.memo(function BodyJointTypeInlineMenu({
  className = "",
  disabled,
  options,
  onAddJointKind,
}) {
  const iconByKind = {
    hinge: JointIcon,
    slide: SlideJointTypeIcon,
    ball: BallJointTypeIcon,
    free: FreeJointTypeIcon,
  };
  const menuItems = (Array.isArray(options) ? options : []).map((option) => ({
    ...option,
    key: option.kind,
    Icon: iconByKind[option.kind] || JointIcon,
  }));

  return (
    <div
      className={[
        "inline-flex shrink-0 flex-nowrap items-center gap-1 rounded-lg border border-gray-600 p-1.5",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {menuItems.map(({ key, label, Icon, disabled: optionDisabled, description }) => {
        const isDisabled = disabled || optionDisabled;
        return (
          <Tooltip key={key} content={description || label}>
            <button
              type="button"
              aria-disabled={isDisabled}
              aria-label={label}
              onClick={(event) => {
                event.stopPropagation();
                if (isDisabled) return;
                onAddJointKind(key);
              }}
              className={
                isDisabled ? BODY_ADD_MENU_DISABLED_BUTTON_CLASS : BODY_ADD_MENU_BUTTON_CLASS
              }
              style={BODY_ADD_MENU_BORDER_STYLE}
            >
              <Icon className="w-4 h-4" aria-hidden="true" />
            </button>
          </Tooltip>
        );
      })}
    </div>
  );
});

export { BodyJointTypeInlineMenu, BodyJointsSection, ChildBodyJointsSection };
